/**
 * Crawlable index of a city's explore-mode pages, grouped by segment.
 *
 * Why this exists: the explore catalogue (restaurants, attractions, spas,
 * beach clubs, nightlife, golf, guides) is browsed through `/discover`, which
 * is the one content route in the app with no route `loader`. Its list is
 * fetched in a `useEffect`, so the HTML a crawler receives contains three
 * internal links. A crawl of every city hub, category hub, home, discover and
 * explore on 2026-08-19 found 102 of 816 English pages receiving an internal
 * link, and zero links pointing at any of the 329 restaurant pages.
 *
 * Those pages return 200 and `index,follow`, and they link to four siblings
 * each — so the catalogue is a well-connected island with no bridge to it.
 * Google could reach it only through the sitemap, and a page with no internal
 * links earns effectively no authority.
 *
 * This section is the bridge, and it is deliberately text-only: 164 links on
 * the Marrakech hub cost ~13 KB as anchors, where server-rendering the whole
 * Discover app would have meant serialising a 422 KB / 778-item index into
 * every page. Rendered on the city overview only (no category filter, no
 * search), where "everything in this city" is the honest framing.
 *
 * ## Why the segments are tabs
 *
 * As eight stacked lists this block ran 3,569px on Marrakech — ninety-two
 * restaurants in one column, thirty-three attractions in another, eight spas
 * in a third, all ending at different depths. One segment at a time measures
 * 402px on the same data: the same links, an eighth of the height.
 *
 * **Every group is rendered on every request.** The inactive ones carry the
 * `hidden` attribute, which is a paint instruction, not an omission: all 164
 * anchors are in the server HTML and a crawler follows every one of them.
 * Unmounting the inactive groups instead would delete the anchors from the
 * HTML and undo the only thing this section exists to do — so do not "optimise"
 * this into `groups.find(active)`.
 *
 * The first group wins the open state, and `SEGMENT_ORDER` opens on
 * attractions: it is what someone landing on a city hub is looking for, and
 * restaurants is both the longest list and the least useful thing to greet
 * them with (owner's call, 2026-09-07).
 */
import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  cityExploreLinksQueryOptions,
  type CityExploreLinkRow,
} from "../api/activities-query";
import { activityHrefParts } from "../lib/activity-href";
import { SEGMENT_LABEL_KEY } from "../lib/explore-segments";

/**
 * Render order for the segment groups, and therefore which one is open when
 * the page loads. Sightseeing leads because it is what a first-time visitor to
 * a city hub is looking for; eating follows. Segments absent from a city are
 * skipped rather than rendered empty.
 */
const SEGMENT_ORDER = [
  "locations",
  "restaurants",
  "spas",
  "beach-clubs",
  "nightlife",
  "golf",
  "stays",
  "guides",
] as const;

/**
 * How many links a phone shows before it offers the rest.
 *
 * A phone renders this list in one column, so on Marrakech "Attractions" ran
 * 945px and "Restaurants" would have run about 2,527px — a wall of names with
 * no end in sight. Twelve plus a button is 406px for any segment, whatever its
 * size. Two columns was tried first and rejected: it only reached 680px and
 * nine of the thirty-three names wrapped onto a second line, which read worse
 * than the height it saved.
 *
 * From `sm` up the grid is two then four columns and the whole list fits, so
 * the cap and its button exist only on phones. Both are done in CSS rather
 * than by measuring the viewport in JS, so the server and the client render
 * the same HTML and every link is in it either way.
 */
const PHONE_VISIBLE = 12;

interface CityExploreLinksProps {
  citySlug: string;
  cityName: string | null;
  lang: string;
}

export function CityExploreLinks({
  citySlug,
  cityName,
  lang,
}: CityExploreLinksProps) {
  const { t } = useTranslation();
  const { data: rows } = useSuspenseQuery(
    cityExploreLinksQueryOptions(citySlug, lang),
  );

  const groups = useMemo(() => {
    const bySegment = new Map<string, CityExploreLinkRow[]>();
    for (const row of rows) {
      const segment = row.segment?.trim();
      // An unknown segment has no label in any locale and no obvious place in
      // the order, so it is dropped here rather than rendered untranslated.
      if (!segment || !SEGMENT_LABEL_KEY[segment]) continue;
      const bucket = bySegment.get(segment);
      if (bucket) bucket.push(row);
      else bySegment.set(segment, [row]);
    }
    return SEGMENT_ORDER.filter((segment) => bySegment.has(segment)).map(
      (segment) => ({
        segment,
        label: t(SEGMENT_LABEL_KEY[segment]),
        rows: bySegment
          .get(segment)!
          .slice()
          .sort((a, b) => a.title.localeCompare(b.title, lang)),
      }),
    );
  }, [rows, t, lang]);

  // Null rather than a slug, so the open tab follows the data: a city without
  // attractions opens on whatever it does have, and the fallback survives a
  // segment disappearing between renders.
  const [chosen, setChosen] = useState<string | null>(null);
  /* Per segment, so opening one long list does not expand the others. */
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const active =
    groups.find((group) => group.segment === chosen)?.segment ??
    groups[0]?.segment;

  if (groups.length === 0) return null;

  const heading = cityName
    ? t("activity.explore_in_city", {
        defaultValue: "Explore {{city}}",
        city: cityName,
      })
    : t("activity.explore_heading", { defaultValue: "Explore" });

  return (
    <section aria-labelledby="city-explore-links" className="py-10">
      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#5a5d77]">
        {t("activity.explore_eyebrow", { defaultValue: "Browse the city" })}
      </p>
      <h2
        id="city-explore-links"
        className="mt-1 text-[20px] font-bold leading-tight text-[#110E39] sm:text-[22px]"
        style={{ fontFamily: "var(--font-geist)" }}
      >
        {heading}
      </h2>

      {/*
        Wrapped, never a sideways scroll. A single group is not worth a tab
        row, so a city with one segment goes straight to its list.
      */}
      {groups.length > 1 ? (
        <div
          role="group"
          aria-label={t("activity.explore_tablist", {
            defaultValue: "Choose what to browse",
          })}
          className="mt-4 flex flex-wrap gap-2"
        >
          {groups.map((group) => {
            const on = group.segment === active;
            return (
              <button
                key={group.segment}
                type="button"
                onClick={() => setChosen(group.segment)}
                aria-pressed={on}
                aria-controls={`city-explore-${group.segment}`}
                className={`inline-flex min-h-[32px] items-center gap-1.5 rounded-full px-3.5 text-[13px] font-semibold transition ${
                  on
                    ? "bg-[#110E39] text-white"
                    : "bg-white text-[#110E39] ring-1 ring-[rgba(17,14,57,0.16)] hover:ring-[rgba(17,14,57,0.32)]"
                }`}
              >
                {group.label}
                <span className={on ? "text-white/60" : "text-[#5a5d77]"}>
                  {group.rows.length}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}

      {groups.map((group) => (
        // `hidden`, not unmounted: see the note at the top of this file. The
        // anchors below have to reach the crawler on every request, whichever
        // tab a visitor happens to be looking at.
        <div
          key={group.segment}
          id={`city-explore-${group.segment}`}
          hidden={group.segment !== active}
          className="mt-4"
        >
          {/*
            The link to that segment's city hub
            (/{lang}/category/restaurants/marrakech). Without it those hubs
            would be orphans themselves — they rank for the head term
            ("restaurants in Marrakech") but nothing on the site points at
            them, which is the exact problem this section exists to fix.
          */}
          <h3 className="text-[13px] font-bold uppercase tracking-[0.08em] text-[#110E39]">
            <Link
              to="/$lang/category/$category/$city"
              params={{ lang, category: group.segment, city: citySlug }}
              className="inline-flex items-center gap-1 py-1.5 transition hover:text-[color:var(--color-primary)]"
            >
              {group.label}
              <span aria-hidden>→</span>
            </Link>
          </h3>

          <ul className="mt-1 grid gap-x-6 sm:grid-cols-2 lg:grid-cols-4">
            {group.rows.map((row, index) => {
              const href = activityHrefParts({
                lang,
                citySlug: row.citySlug ?? citySlug,
                categorySlug: row.categorySlug,
                segment: row.segment,
                slug: row.slug,
              });
              return (
                <li
                  key={row.id}
                  /*
                    `hidden sm:list-item`, not a slice: the anchor stays in the
                    document at every width, which is the whole point of this
                    section. Only the phone hides the overflow, and only until
                    the button below is pressed.
                  */
                  className={
                    index >= PHONE_VISIBLE && !expanded[group.segment]
                      ? "hidden sm:list-item"
                      : undefined
                  }
                >
                  <Link
                    to={href.to}
                    params={href.params}
                    // 24px min hit area (WCAG 2.5.8) via the block padding,
                    // since these sit close together in a dense list.
                    className="block py-1 text-[13.5px] leading-[1.45] text-[#5a5d77] transition hover:text-[color:var(--color-primary)] hover:underline"
                  >
                    {row.title}
                  </Link>
                </li>
              );
            })}
          </ul>

          {/*
            Phone only: `sm:hidden` because from `sm` up nothing was hidden in
            the first place, so there would be nothing for it to reveal.
          */}
          {group.rows.length > PHONE_VISIBLE && !expanded[group.segment] ? (
            <button
              type="button"
              onClick={() =>
                setExpanded((current) => ({ ...current, [group.segment]: true }))
              }
              className="mt-2 inline-flex min-h-[32px] items-center rounded-full border border-[rgba(17,14,57,0.16)] bg-white px-4 text-[13px] font-semibold text-[#110E39] transition hover:border-[rgba(17,14,57,0.32)] sm:hidden"
            >
              {t("activity.explore_show_all", {
                defaultValue: "Show all {{count}}",
                count: group.rows.length,
              })}
            </button>
          ) : null}
        </div>
      ))}
    </section>
  );
}
